import { useMemo } from "react";
import { Link } from "react-router-dom";
import EmptyState from "@/components/base/EmptyState";
import NewsCard from "@/components/feature/NewsCard";
import ActorCard from "@/components/feature/ActorCard";
import WorkCard from "@/components/feature/WorkCard";
import { searchContent } from "@/lib/content";

export default function SearchResults({ keyword, onClear }: { keyword: string; onClear: () => void }) {
  const result = useMemo(() => searchContent(keyword), [keyword]);
  const total = result.news.length + result.works.length + result.actors.length;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <p className="text-sm text-foreground-600">
          搜索关键词 <span className="text-accent-600 font-medium">“{keyword}”</span>，共找到
          <span className="text-primary-600 font-bold mx-1">{total}</span>条相关内容
        </p>
        <button
          type="button"
          onClick={onClear}
          className="text-sm text-foreground-500 hover:text-primary-600 transition-colors flex items-center gap-1 cursor-pointer"
        >
          <i className="ri-close-line" />
          清空搜索
        </button>
      </div>

      {total === 0 ? (
        <EmptyState
          icon="ri-search-eye-line"
          title="暂无相关资讯"
          description="没有找到与该关键词匹配的资讯、作品或人物，换个词试试吧。"
          onReset={onClear}
          actionTo="/"
          actionLabel="返回首页"
        />
      ) : (
        <div className="flex flex-col gap-12">
          {result.news.length > 0 && (
            <section>
              <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-article-line text-primary-500" />
                相关资讯（{result.news.length}）
              </h3>
              <div className="flex flex-col gap-4">
                {result.news.map((news) => (
                  <NewsCard key={news.id} news={news} />
                ))}
              </div>
            </section>
          )}

          {result.works.length > 0 && (
            <section>
              <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-film-line text-primary-500" />
                相关作品（{result.works.length}）
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {result.works.map((work) => (
                  <WorkCard key={work.id} work={work} />
                ))}
              </div>
            </section>
          )}

          {result.actors.length > 0 && (
            <section>
              <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-user-star-line text-primary-500" />
                相关人物（{result.actors.length}）
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {result.actors.map((actor) => (
                  <ActorCard key={actor.id} actor={actor} />
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      <div className="mt-10 pt-6 border-t border-background-200">
        <Link to="/works" className="text-sm text-primary-600 hover:text-primary-700 transition-colors inline-flex items-center gap-1">
          也可以前往作品页浏览完整片单
          <i className="ri-arrow-right-line" />
        </Link>
      </div>
    </div>
  );
}